import {EventEmitter, Injectable, TemplateRef} from '@angular/core';
import {BehaviorSubject, Subscription}         from 'rxjs';
import {ApDynformsConfigFieldset}              from '../../../../ap-dynforms/config/ap-dynforms-config-fieldset';
import {ApDynformsComponent}                   from '../../../../ap-dynforms/ap-dynforms.component';
import {
  IPpProductAvailableCrops,
  IPpProductSelectedDetails,
  IPpProductWizardDetailsData,
  IPpProductWizardDetailsResponsiveData,
  IPpProductWizardProvider
}                                              from '../pp-product-wizard.types';
import {ApDynGridColumnConfigBase}             from '../../../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {PpProductWizardDetailsUiProvider}      from './pp-product-wizard-details.ui-provider';
import {nameOf}                                from '../../../../ap-core/utils/ap-name-of';
import {ApRoleTypeService}                     from '../../../../services/common/ap-role-type.service';
import {LoginStore}                            from '../../../../stores/login/login.store';
import {UnitsStore}                            from '../../../../stores/common/units.store';
import {EffectiveRangesStore}                  from '../../../../stores/common/effective-ranges.store';
import {CropTypeStore}                         from '../../../../stores/base-data/crop.types.store';
import {ApGuidUtil}                            from '../../../../ap-utils';
import {ApUtilService}                         from '../../../../ap-utils/service/ap-util.service';
import {ApTranslationService}                  from '../../../../ap-utils/service/ap-translation.service';
import {ApDynGridsComponent}                   from '../../../../ap-dyngrids/ap-dyngrids.component';
import {ApDynformsValidator}                   from '../../../../ap-dynforms/ap-dynforms-validator';
import {ApDateRangeValidator}                  from '../../../../ap-dynforms/validators/ap-date-range-validator';
import {WorktypeUnitStore}                     from '../../../../stores/base-data/worktype-unit.store';
import {SelectionEvent}                        from '@progress/kendo-angular-grid';
import {CountryStore}                          from '../../../../stores/common/country.store';

/**
 * PP-product wizard details-tab business logic provider
 */
@Injectable()
export class PpProductWizardDetailsProvider implements IPpProductWizardProvider {
  public productDetailsColumns$: BehaviorSubject<ApDynGridColumnConfigBase[]> = new BehaviorSubject<ApDynGridColumnConfigBase[]>([]);
  public availableCropsColumns: ApDynGridColumnConfigBase[] = [];
  public formValidators = [
    new ApDynformsValidator({
      validator: ApDateRangeValidator.ValidateDateRange('PermissionStart', 'PermissionEnd'),
      errorKey: 'AgriConnect_Error_MinMax'
    }),
    new ApDynformsValidator({
      validator: ApDateRangeValidator.ValidateDateRange('PermissionEnd', 'UseByDate'),
      errorKey: 'AgriConnect_Error_MinMax'
    })
  ];
  private _responsiveData: IPpProductWizardDetailsResponsiveData;
  private _subscriptions: Subscription[] = [];

  /**
   * form properties which might trigger the ui to rebuild or other values to change
   */
  private _responsiveProperties = [
    nameOf<IPpProductWizardDetailsData>('IsCountryWide'),
    nameOf<IPpProductWizardDetailsData>('PermissionStart'),
    nameOf<IPpProductWizardDetailsData>('PermissionEnd'),
    nameOf<IPpProductWizardDetailsData>('UseByDate'),
    nameOf<IPpProductWizardDetailsData>('WorkType'),
    nameOf<IPpProductWizardDetailsData>('GrowStageMax'),
    nameOf<IPpProductWizardDetailsData>('GrowStageMin'),
    nameOf<IPpProductWizardDetailsData>('MaxRate'),
    nameOf<IPpProductWizardDetailsData>('EffectiveRanges'),
    nameOf<IPpProductWizardDetailsData>('Unit')
  ];
  private _dynForm: ApDynformsComponent;
  /**
   * These values might change during the lifetime of this wizard(step)
   */
  private _formDto: IPpProductWizardDetailsData = undefined;
  private _caption$: BehaviorSubject<string>;
  private _fieldSets$: BehaviorSubject<ApDynformsConfigFieldset[]>;
  private _ppProductSelectedDetailsTemplate: TemplateRef<any>;
  private _ppProductAvailableCropsTemplate: TemplateRef<any>;
  private _ppProductAvailableCropsGrid$: BehaviorSubject<ApDynGridsComponent>;
  private _addDetailsCallback: EventEmitter<any>;
  private _deleteDetailsClicked = ApUtilService.createEventEmitter(this.deleteProductDetails.bind(this), this._subscriptions);
  private _previouslySelectedCropIds: number[] = [];

  constructor(private loginStore: LoginStore,
              private unitsStore: UnitsStore,
              private countryStore: CountryStore,
              private cropTypeStore: CropTypeStore,
              private effectiveRangesStore: EffectiveRangesStore,
              private translationService: ApTranslationService,
              private worktypeUnitStore: WorktypeUnitStore,
              private ppProductWizardDetailsUiProvider: PpProductWizardDetailsUiProvider) {
  }

  public init(caption$: BehaviorSubject<string>,
              fieldSets$: BehaviorSubject<ApDynformsConfigFieldset[]>,
              dynForm: ApDynformsComponent,
              selectedDetailsItems$: BehaviorSubject<IPpProductSelectedDetails[]>,
              ppProductAvailableCropsGrid$: BehaviorSubject<ApDynGridsComponent>,
              ppProductSelectedDetailsTemplate: TemplateRef<any>,
              ppProductAvailableCropsTemplate: TemplateRef<any>): void {
    this._caption$ = caption$;
    this._fieldSets$ = fieldSets$;
    this._dynForm = dynForm;
    this._ppProductSelectedDetailsTemplate = ppProductSelectedDetailsTemplate;
    this._ppProductAvailableCropsTemplate = ppProductAvailableCropsTemplate;
    this._ppProductAvailableCropsGrid$ = ppProductAvailableCropsGrid$;
    this._addDetailsCallback = ApUtilService.createEventEmitter(this.addDetails.bind(this), this._subscriptions);
    this._previouslySelectedCropIds = [];
    this.countryStore.loadAllCountries();
    this.availableCropsColumns = this.ppProductWizardDetailsUiProvider.getAvailableCropsColumns();
    this.productDetailsColumns$.next(this.ppProductWizardDetailsUiProvider.getProductDetailsColumns(this._deleteDetailsClicked, this._formDto?.Unit));
    this._subscriptions.push(this._ppProductAvailableCropsGrid$.subscribe(availableCropsGrid =>
      this._subscriptions.push(availableCropsGrid?.gridPaging?.onSelectedChange.subscribe(() => this.updateAddDetailsButtonAvailability()))));
    this._responsiveData = {
      CountryDisabled$: new BehaviorSubject(true),
      UnitDisabled$: new BehaviorSubject(true),
      MinPermissionEnd$: new BehaviorSubject(new Date()),
      MinUseByDate$: new BehaviorSubject(new Date()),
      PreselectedGrowStageMax$: new BehaviorSubject(99),
      PreselectedGrowStageMin$: new BehaviorSubject(0),
      AddDetailsButtonDisabled$: new BehaviorSubject(true),
      PreselectedUnit$: new BehaviorSubject(undefined),
      SelectedDetailsItems$: selectedDetailsItems$,
      AvailableUnits$: new BehaviorSubject([])
    };
  }

  /**
   * Main method for handling user input.
   * Depending on the changed form values other values get changed or the ui is rebuilt
   * @param valueChangesArgs eventArgs from form's valueChanged event
   */
  public onFormValueChanges(valueChangesArgs: { control: string; value: any }): void {
    /**
     * we need to update UI, Data or Observables only after user changed certain properties:
     */
    if (!this._responsiveProperties.Exists(p => p === valueChangesArgs.control)) {
      return;
    }

    /**
     * retrieve current values from the form
     * and modify its values if needed or rebuild the UI if needed
     */
    this._formDto = this._dynForm?.form?.getRawValue() as IPpProductWizardDetailsData;
    if (!this._formDto) {
      return;
    }

    /**
     * Enable/disable country selection depending on checkbox and user's role-ID
     */
    if (valueChangesArgs?.control === nameOf<IPpProductWizardDetailsData>('IsCountryWide')) {
      this._responsiveData?.CountryDisabled$?.next(!this._formDto.IsCountryWide || !ApRoleTypeService.hasAgriconRole(this.loginStore.User));
      this._formDto.Country = undefined;
    }

    /**
     * Take care of permission date ranges and boundaries
     */
    if (this._responsiveData?.MinPermissionEnd$?.value !== this._formDto.PermissionStart) {
      this._responsiveData?.MinPermissionEnd$.next(this._formDto.PermissionStart);
    }
    if (this._responsiveData?.MinUseByDate$?.value !== this._formDto.PermissionEnd) {
      this._responsiveData?.MinUseByDate$.next(this._formDto.PermissionEnd);
    }

    /**
     * Take care of worktype-unit mapping. The user should be able to select units which are mapped
     * to the selected worktype
     */
    if (valueChangesArgs?.control === nameOf<IPpProductWizardDetailsData>('WorkType')) {
      this._responsiveData.AvailableUnits$.next(
        this.worktypeUnitStore.getUnitsByWorktype(this._formDto.WorkType)
      );
      this._responsiveData.UnitDisabled$.next(!this._formDto.WorkType);
      // reset unit if it is not valid for the currently selected worktype
      if (!this._responsiveData.AvailableUnits$.value?.Any(u => u?.Id === this._formDto.Unit)) {
        this._formDto.Unit = undefined;
      }
    }

    /**
     * Take care of unit handling. The selected unit needs to appear right next to the maxRate label
     * as well as in the details-grid header
     */
    if (valueChangesArgs?.control === nameOf<IPpProductWizardDetailsData>('Unit') ||
      valueChangesArgs?.control === nameOf<IPpProductWizardDetailsData>('WorkType')) {
      this._responsiveData?.PreselectedUnit$.next(
        this.translationService.translate(
          this.unitsStore.Listen(s => s.data).value?.FirstOrDefault(u => u.Id === this._formDto.Unit)?.Key)
      );
      this.productDetailsColumns$.next(
        this.ppProductWizardDetailsUiProvider.getProductDetailsColumns(this._deleteDetailsClicked, this._formDto?.Unit)
      );
    }

    /**
     * Take care of growStage boundaries
     */
    if (valueChangesArgs?.control === nameOf<IPpProductWizardDetailsData>('GrowStageMax')) {
      this._responsiveData?.PreselectedGrowStageMax$.next(this._formDto.GrowStageMax);
    }
    if (valueChangesArgs?.control === nameOf<IPpProductWizardDetailsData>('GrowStageMin')) {
      this._responsiveData?.PreselectedGrowStageMin$.next(this._formDto.GrowStageMin);
    }

    /**
     * Patch back our updated dto and update the button availability
     */
    this.updateAddDetailsButtonAvailability();
    this.patchForm();
  }

  /**
   * Handler adding a product's detail to the result grid
   */
  public addDetails(): void {
    this._formDto = this._dynForm.form.getRawValue() as IPpProductWizardDetailsData;
    if (!this._formDto) {
      return;
    }
    const selectedDetails = this._responsiveData.SelectedDetailsItems$.value;
    const crops = this._ppProductAvailableCropsGrid$?.value?.gridPaging.selectedItems as IPpProductAvailableCrops[];
    const effectiveRangeNames = this._formDto.EffectiveRanges?.map(e =>
      this.translationService.translate(this.effectiveRangesStore.getEffectiveRangeById(e)?.Key)).Join(', ');

    const detailToAdd = {
      Id: ApGuidUtil.generateNewGuid(),
      CropTypeNames: crops?.map(c => c?.CropTypeName)?.Join(', '),
      CropTypeIds: crops?.map(c => c?.Id),
      GrowStageMax: this._formDto.GrowStageMax,
      GrowStageMin: this._formDto.GrowStageMin,
      MaxRate: this._formDto.MaxRate,
      EffectiveRanges: effectiveRangeNames,
      EffectiveRangeIds: this._formDto.EffectiveRanges,
      Indicator: this._formDto.Indicator,
      ButtonIconName: 'trashIcon'
    };

    // reset certain fields after the user added the details...
    this._formDto.GrowStageMin = undefined;
    this._formDto.GrowStageMax = undefined;
    this._formDto.MaxRate = undefined;
    this.patchForm();
    this.updateAddDetailsButtonAvailability();
    this._responsiveData?.SelectedDetailsItems$.next([...selectedDetails, ...[detailToAdd]]);
  }

  /**
   * Handler deleting a details from the result grid
   */
  public deleteProductDetails(dataItem: IPpProductSelectedDetails): void {
    const selectedDetails = this._responsiveData?.SelectedDetailsItems$?.getValue();
    this._responsiveData?.SelectedDetailsItems$.next(selectedDetails?.filter(d => d?.Id !== dataItem?.Id));
  }

  public updateCaption(): void {
    this._caption$?.next('Create_pp_product_details');
  }

  public updateFieldSets(): void {
    this._fieldSets$?.next(
      this.ppProductWizardDetailsUiProvider.createFieldset(this._responsiveData, this._addDetailsCallback,
        this._ppProductSelectedDetailsTemplate, this._ppProductAvailableCropsTemplate));
    // select crops which were selected when the user was on details-tab the last time
    setTimeout(() => {
      if (this._previouslySelectedCropIds?.length > 0) {
        this._ppProductAvailableCropsGrid$.value?.gridPaging?.setSelectedKeys(this._previouslySelectedCropIds);
        this._ppProductAvailableCropsGrid$.value?.gridPaging?.updateGridView();
      }
    }, 0);
  }

  private updateAddDetailsButtonAvailability(): void {
    const formInvalid = !((this._formDto?.GrowStageMin != null && this._formDto?.GrowStageMin >= 0) && this._formDto?.GrowStageMax > 0 &&
      this._formDto?.MaxRate > 0 && this._formDto?.EffectiveRanges?.length > 0);
    const cropSelectionInvalid = this._ppProductAvailableCropsGrid$?.value?.gridPaging?.selectedKeys?.length <= 0;
    this._responsiveData.AddDetailsButtonDisabled$.next(formInvalid || cropSelectionInvalid);
  }

  /**
   * Method applies the current data transfer object (formDto) to the reactive form
   * without emitting new events to avoid triggering onFormValueChanges again.
   * @param triggerValidation true to trigger validations for the whole form
   * @private
   */
  private patchForm(triggerValidation?: boolean): void {
    setTimeout(() => {
      this._dynForm.form.patchValue(this._formDto, {emitEvent: false});
      if (triggerValidation) {
        setTimeout(() => this._dynForm.markAsTouched(), 0);
      }
    }, 0);
  }

  public buildDefaultDetails(): IPpProductWizardDetailsData {
    return {
      ProductName: '',
      IsCountryWide: false,
      Country: '',
      Note: '',
      PermissionStart: new Date(),
      PermissionEnd: undefined,
      UseByDate: undefined,
      WorkType: undefined,
      Unit: undefined,
      AvailableCrops: this.cropTypeStore.AvailableCropTypes,
      SelectedDetails: [],
      GrowStageMin: undefined,
      GrowStageMax: undefined,
      MaxRate: undefined,
      EffectiveRanges: undefined,
      Indicator: '',
      SelectedProductDetails: []
    };
  }

  /**
   * Handles the selectionChange of product details.
   * When selectionChanges the selected details shall be adopted to
   * the agronomic fields
   * @param selectionArgs
   */
  public onDetailsSelectionChange(selectionArgs: SelectionEvent): void {
    if (selectionArgs?.selectedRows?.length <= 0) {
      return;
    }

    const selectedDetails = selectionArgs.selectedRows[0].dataItem as IPpProductWizardDetailsData;
    if (!selectedDetails) {
      return;
    }

    // Reset growStage boundaries because previous values might have impact on the current update
    // and might reset the values in case they are exceeding the old boundaries
    this._responsiveData.PreselectedGrowStageMax$.next(undefined);
    this._responsiveData.PreselectedGrowStageMin$.next(undefined);

    this._formDto = this._dynForm.form.getRawValue() as IPpProductWizardDetailsData;
    this._formDto.GrowStageMin = selectedDetails?.GrowStageMin;
    this._formDto.GrowStageMax = selectedDetails?.GrowStageMax;
    this._formDto.MaxRate = selectedDetails?.MaxRate;
    this.patchForm(false);
  }

  /**
   * Custom check if the form is valid. The regular check of dynComponent.Valid$ does
   * not work here because it would check all controls - but product gs min/max, maxRate and effectiveRange
   * are not required to be validated before saving. Anyway, those controls need a required validator
   * to validate before adding the product details to our details list
   */
  public isValid(): boolean {
    const formControls = this._dynForm?.form?.controls;
    return formControls &&
      formControls[nameOf<IPpProductWizardDetailsData>('ProductName')]?.valid &&
      formControls[nameOf<IPpProductWizardDetailsData>('PermissionStart')]?.valid &&
      formControls[nameOf<IPpProductWizardDetailsData>('UseByDate')]?.valid &&
      formControls[nameOf<IPpProductWizardDetailsData>('PermissionEnd')]?.valid &&
      formControls[nameOf<IPpProductWizardDetailsData>('WorkType')]?.valid &&
      formControls[nameOf<IPpProductWizardDetailsData>('Unit')]?.valid;
  }

  /**
   * Method which handles operations when leaving details tab
   */
  public leave(): void {
    const selectedItems = this._ppProductAvailableCropsGrid$?.value?.gridPaging?.selectedItems as IPpProductAvailableCrops[];
    this._previouslySelectedCropIds = selectedItems?.map(c => c?.Id);
  }

  public getSelectedDetailsGridHeight(): string {
    return '160px';
  }

  /**
   * Cleanup the mess...
   */
  public onDestroy(): void {
    this._previouslySelectedCropIds = [];
    this._responsiveData = undefined;
    this._formDto = undefined;
    this._subscriptions?.forEach(s => s?.unsubscribe());
    this._subscriptions = [];
  }
}
